import { HISTORIES_ACTION_NAME, PROCESS_STATUS, SORT_METHODS, CONFIG_FIELD_ORDER, DATE_FORMAT } from "src/constant/common";
import { cloneDeep, endsWith, isNaN, omit } from "lodash";
import isEmpty from "lodash/isEmpty";
import { format, isValid, parseISO } from "date-fns";

function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"] : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + " " + units[u];
}

const transformDataVersionsToOptions = (array) => {
  const result = [];

  array.map((item) =>
    result.push({
      label: item.name,
      value: item.name,
      execute_files: transformDataExecutionFileToOptions(item.execute_files),
    })
  );

  return result;
};

const transformDataExecutionFileToOptions = (array) => {
  const result = [];

  array.map((item) => result.push({ label: item.name, value: item.name }));

  return result;
};

const getFileName = (path) => {
  if (!path) return "";
  const regex = /\/?([^/]+\.json|[^/]+\.csv|[^/]+\.xlsx|[^/]+\.xls|[^/]+\.opi)(?:\?|$)/;
  const match = path.match(regex);
  const fileName = match ? match[1] : "";
  return fileName;
};

const getFileUploadName = (path) => {
  if (!path) return "";

  // Decode the path multiple times to handle nested encoding
  let decodedPath = path;
  try {
    while (decodedPath !== decodeURIComponent(decodedPath)) {
      decodedPath = decodeURIComponent(decodedPath);
    }
  } catch (error) {
    console.log("Error decoding path:", path);
  }

  const regex = /\/?([^/]+\.json|[^/]+\.csv|[^/]+\.xlsx|[^/]+\.xls|[^/]+\.opi)(?:\?|$)/;
  const match = decodedPath.match(regex);
  const fileName = match ? match[1] : "";
  return fileName;
};

const getFileType = (path) => {
  if (!path) return "";
  return path.split(".").pop();
};

const uniqArray = (arr) => {
  const seen = new Set();
  const uniqueByDataField = arr.filter((item) => {
    if (!seen.has(item.dataField)) {
      seen.add(item.dataField);
      return true;
    }
    return false;
  });
  return uniqueByDataField;
};

const handleScrollTo = (top, left, id) => {
  setTimeout(() => {
    let leftPosition = left;
    if (id) {
      const element = document.getElementById(id);
      leftPosition = parseInt(element?.getBoundingClientRect().top - left);
    }
    window.scrollTo(top, leftPosition - 85);
  }, 500);
};

const handleScrollToTop = (elementId = null, behavior = "instant") => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior, block: "start" });
  } else {
    window.scrollTo({ top: 0, behavior });
  }
};

const sortData = (data, key, methods = SORT_METHODS.ASC) => {
  return data.sort(function (a, b) {
    let firstChar = a;
    let secondChar = b;
    if (methods === SORT_METHODS.DESC) {
      firstChar = b;
      secondChar = a;
    }
    return firstChar[key].localeCompare(secondChar[key]);
  });
};

const uuid = () => {
  const timestamp = Date.now();
  const randomSegment = Math.floor(Math.random() * 10000);
  const uniqueId = timestamp * 10000 + randomSegment;
  return uniqueId;
};

const extractIDsFromString = (str) => {
  if (!str) return ["", ""];
  const regex = /([^/]+)\/([^/]+)\.json/;
  const match = str.match(regex);
  if (match) {
    return [match[1], match[2]];
  } else {
    return ["", ""];
  }
};

const transformProcessDataToOptions = (array) => {
  const result = [];

  array.map((item) =>
    result.push({
      label: item.name,
      value: item.id,
    })
  );

  return result;
};

const getConfigFromId = (processId, array, key) => {
  const result = array.filter((item) => item.id === processId);
  if (!result.length || !Object.keys(result[0]?.config).length) return {};
  const config = result[0].config?.[key] || {};
  const outputConfig = [];
  CONFIG_FIELD_ORDER.forEach((key) => {
    if (config[key]) {
      outputConfig.push({ name: key, type: config[key] });
    }
  });
  return outputConfig;
};

const getConfigFileName = (config) => {
  const outputPath = [];
  if (config?.no_wind_path) {
    outputPath.push(getFileName(config.no_wind_path));
  }
  if (config?.blow_path) {
    outputPath.push(getFileName(config.blow_path));
  }
  if (config?.atmospheric_pressure_data_path) {
    outputPath.push(getFileName(config.atmospheric_pressure_data_path));
  }
  return outputPath.join(", ");
};

const transformThrustCalculation = (data) => {
  return {
    name: data.name,
    number_of_stage: parseInt(data.number_of_stage, 10),
    input_data: {
      number_of_stage: parseInt(data.number_of_stage, 10),
      stages: data.stage.map((s, index) => ({
        stage: data.stage?.length - index,
        total_propellant_mass: {
          value: parseFloat(s.total_propellant_mass, 10),
          unit: s.unit,
        },
      })),
      modes: [].concat(
        ...data.stage.map((s, index) =>
          s.modes.map((m) => ({
            stage: data.stage?.length - index,
            name: m.name,
            engine_components: m.engine_components.map((c) => ({
              component_id: c.component_id,
              number_of_unit: parseInt(c.number_of_unit, 10),
            })),
            propellant: {
              value: parseFloat(s.total_propellant_mass, 10) * (parseFloat(m.propellant_percentage, 10) / 100),
              unit: s.unit,
            },
            propellant_percentage: {
              value: parseFloat(m.propellant_percentage, 10),
              unit: "%",
            },
            mode: m.mode,
          }))
        )
      ),
    },
  };
};

const reverseTransformThrustCalculation = (data) => {
  // Helper function to find modes for a particular stage
  function getModesForStage(stageNumber, modes) {
    return modes
      .filter((mode) => mode.stage === stageNumber)
      .map((mode) => ({
        name: mode.name,
        propellant_percentage: mode.propellant_percentage.value.toString(),
        mode: mode.mode,
        engine_components: mode.engine_components.map((comp) => ({
          component_id: comp.component_id,
          number_of_unit: comp.number_of_unit.toString(),
        })),
      }));
  }

  // Helper function to calculate total propellant mass based on modes and to get unit
  function calculateTotalPropellantMassAndUnit(stageNumber, modes) {
    let totalMass = 0;
    let unit = "";
    modes
      .filter((mode) => mode.stage === stageNumber)
      .forEach((mode) => {
        totalMass += mode.propellant.value;
        unit = mode.propellant.unit; // Assuming all propellant units are the same within a stage
      });
    return { totalMass, unit };
  }

  return {
    name: `Copy of ${data.name}`,
    number_of_stage: data.number_of_stage,
    stage: data.input_data.stages.map((s) => {
      const { totalMass, unit } = calculateTotalPropellantMassAndUnit(s.stage, data.input_data.modes);
      return {
        total_propellant_mass: s?.total_propellant_mass?.value || totalMass.toString(),
        unit: s?.total_propellant_mass?.unit || unit,
        modes: getModesForStage(s.stage, data.input_data.modes),
      };
    }),
  };
};

const roundToTwoDecimals = (inputNum, force) => {
  let num = Number(inputNum);
  if (isNaN(num) || (Number.isInteger(num) && !force)) return inputNum;
  return num.toFixed(2);
};

const mergeArrays = (array1, array2) => {
  if (!array2 || !Array.isArray(array2)) return array1;
  return array1.map((item, index) => {
    return { ...item, ...array2[index] };
  });
};

const mergeUniqueObjects = (arr1, arr2) => {
  const mergedArray = [...arr1, ...arr2];
  const seenItems = new Set();

  const serialize = (obj) => JSON.stringify(obj);
  const uniqueItems = mergedArray.reduce((uniqueList, item) => {
    const itemString = serialize(item);

    if (!seenItems.has(itemString)) {
      uniqueList.push(item);
      seenItems.add(itemString);
    }

    return uniqueList;
  }, []);

  return uniqueItems;
};

const getChartData = (mainList, listOfPoints = []) => {
  if (!Array.isArray(mainList)) return { data: [], labels: [] };

  const labels = [];
  const data = [];
  const pointRadius = [];

  let combinedList = [...mainList];

  if (listOfPoints?.length > 0) {
    const uniqueList = mergeUniqueObjects(mainList, listOfPoints);
    const formattedPoints = uniqueList.map((point) => {
      if (listOfPoints?.find((l) => l[0] === point[0] && l[1] === point?.[1])) {
        return { ...point, isPoint: true };
      }

      return point;
    });

    combinedList = [...formattedPoints];
  }

  combinedList
    .filter((item) => typeof item[0] === "number")
    .sort((a, b) => a[0] - b[0])
    .forEach((item) => {
      labels.push(item[0]);
      data.push(item[1]);
      pointRadius.push(item.isPoint ? 3 : 0);
    });

  const uniqueListPoints = Array.from(new Set(listOfPoints.map((point) => point[0])));

  return {
    labels,
    data,
    pointRadius,
    listPoint: uniqueListPoints,
  };
};

const uniqueArray = (array, key) => {
  const uniqueArray = [];
  const ids = new Set();

  array.forEach((item) => {
    if (!ids.has(item[key])) {
      ids.add(item[key]);
      uniqueArray.push(item);
    }
  });

  return uniqueArray;
};

const isCSVContent = (content) => {
  if (typeof content !== "string") {
    return false;
  }
  const csvPattern = /^,?(?:[^,\n]+,)*[^,\n]+(?:\n|$)/;
  return csvPattern.test(content);
};

const MAPPING_CONTENT_CHANGE_TO_TEXT = (t, _, id, new_value) => ({
  [HISTORIES_ACTION_NAME.UPDATE_ROCKET_MODEL]: () => t("logs.model"),
  [HISTORIES_ACTION_NAME.UPDATE_ENGINE_COMPONENT]: () => t("logs.engined"),
  [HISTORIES_ACTION_NAME.UPDATE_COMPONENT_DETAIL]: () => t("logs.components"),
  [HISTORIES_ACTION_NAME.ADD_NEW_CALCULATION]: () => {
    return t("logs.add_new_calculation", {
      unit: Array.isArray(new_value) ? (new_value?.length > 1 ? "s" : "") : "",
      ids: Array.isArray(new_value) ? new_value?.map((i) => i?.id)?.join(", ") : "",
    });
  },
  [HISTORIES_ACTION_NAME.RUN_SIMULATION]: () => {
    if (new_value?.simulator_status === PROCESS_STATUS.SUCCEEDED) {
      return t("logs.run_simulation_success", { status: PROCESS_STATUS.SUCCEEDED });
    }

    return t("logs.run_simulation_fail", { status: PROCESS_STATUS.FAILED });
  },
  [HISTORIES_ACTION_NAME.CALCULATE_THRUST]: () => {
    if (new_value?.status === PROCESS_STATUS.SUCCEEDED) {
      return t("logs.thrust_success", { status: PROCESS_STATUS.SUCCEEDED, id });
    }

    return t("logs.thrust_fail", { status: PROCESS_STATUS.FAILED, id });
  },
  [HISTORIES_ACTION_NAME.SAVE_UNPROCESSED_COMPONENT_DETAILS]: () => t("logs.save_unprocessed_component_details"),
});

const sumArrayWithKey = (data, key) => {
  if (data?.length === 0) return;

  return data.reduce((accumulator, currentValue) => {
    if (!currentValue[key]) return accumulator;

    return preciseAdd(accumulator, parseFloat(currentValue[key]));
  }, 0);
};

const mergeChildren = (arr) => {
  let result = [];

  const flatten = (array) => {
    for (let obj of array) {
      if (obj.children) {
        result = result.concat(obj.children);
        flatten(obj.children); // Recursively flatten the children
      }
    }
  };

  flatten(arr?.children);
  return result;
};

const findMaxMin = (data, key) => {
  // Create an array of objects with parsed values alongside their original values
  const parsedData = data
    .map((item) => {
      const value = item[key];
      // Determine if the value can be parsed to a number
      const parsedValue = typeof value === "string" && !isNaN(value) ? parseFloat(value) : value;
      return { original: value, parsed: parsedValue };
    })
    .filter((item) => typeof item.parsed === "number" && (Boolean(item.parsed) || item.parsed === 0)); // Ensure 0 is included

  // Extract original values for max and min by finding max and min parsed values
  const maxParsedValue = parsedData.length ? Math.max(...parsedData.map((item) => item.parsed)) : undefined;
  const minParsedValue = parsedData.length ? Math.min(...parsedData.map((item) => item.parsed)) : undefined;

  const maxOriginalValue = parsedData.find((item) => item.parsed === maxParsedValue)?.original;
  const minOriginalValue = parsedData.find((item) => item.parsed === minParsedValue)?.original;

  return { max: maxOriginalValue, min: minOriginalValue };
};

const calculateProperties = (nodes) => {
  // Recursive function to process each node
  const processNode = (node) => {
    if (node.type === "component") {
      // If the node is a component, return its properties
      return [{ length: node.length, l_axis_position: node.l_axis_position || 0 }];
    } else if (node.children && node.children.length > 0) {
      // If the node has children, process each child
      let childResults = [];
      node.children.forEach((child) => {
        childResults = childResults.concat(processNode(child));
      });
      // Calculate the sum using the calculateSum function
      const calculatedSum = calculateRocketLength(childResults);
      const newNode = { totalLength: calculatedSum };
      // Return the aggregated results to parent nodes
      return [newNode];
    }
    return [];
  };
  // Process each top-level node
  nodes.forEach((node) => {
    node.totalLength = processNode(node)[0].totalLength;
  });
  return nodes;
};

const calculateRocketLength = (items) => {
  if (!items || items.length === 0) return 0;

  // Reduce items to a map of l_axis_position with maximum lengths
  const axisLengths = items.reduce((map, { length, l_axis_position }) => {
    if (!map[l_axis_position] || map[l_axis_position] < length) {
      map[l_axis_position] = length;
    }
    return map;
  }, {});

  // Convert the map to an array and sort by l_axis_position
  const sortedAxisLengths = Object.entries(axisLengths).map(([l_axis_position, length]) => ({
    total: preciseAdd(l_axis_position, length),
    l_axis_position,
  }));

  return findMaxTotalAndMinLengthDifference(sortedAxisLengths) || 0;
};

const findMaxTotalAndMinLengthDifference = (arr) => {
  const { maxTotal, minLength } = arr.reduce(
    (acc, item) => {
      const total = parseFloat(item.total);
      const length = parseFloat(item.l_axis_position);
      return {
        maxTotal: total > acc.maxTotal ? total : acc.maxTotal,
        minLength: length < acc.minLength ? length : acc.minLength,
      };
    },
    { maxTotal: -Infinity, minLength: Infinity }
  );

  return preciseSubtract(maxTotal, minLength);
};

const preciseAdd = (num1, num2) => {
  const factor = Math.pow(10, Math.max(countDecimalPlaces(num1), countDecimalPlaces(num2)));
  return (Math.round(num1 * factor) + Math.round(num2 * factor)) / factor;
};

const preciseSubtract = (num1, num2) => {
  // Helper function to count decimal places
  const countDecimalPlaces = (num) => {
    if (Math.floor(num) === num) return 0;
    return num.toString().split(".")[1].length || 0;
  };

  // Determine the maximum number of decimal places
  const factor = Math.pow(10, Math.max(countDecimalPlaces(num1), countDecimalPlaces(num2)));

  // Perform subtraction with the factor to handle precision
  return (Math.round(num1 * factor) - Math.round(num2 * factor)) / factor;
};

const countDecimalPlaces = (num) => {
  if (!num) return 0;
  if (Math.floor(num) === num) return 0;
  return num.toString().split?.(".")?.[1]?.length || 0;
};

const calculateCenterOfGravity = (items) => {
  if (!items || items?.length === 0) return 0;
  let sumMass = 0;
  let sumGravityX = 0;
  let sumGravityY = 0;
  let sumGravityZ = 0;

  items?.forEach((item) => {
    sumMass = preciseAdd(sumMass, item?.mass);
    sumGravityX = preciseAdd(sumGravityX, parseFloat(item?.center_of_gravity_x) * parseFloat(item?.mass));
    sumGravityY = preciseAdd(sumGravityY, parseFloat(item?.center_of_gravity_y) * parseFloat(item?.mass));
    sumGravityZ = preciseAdd(sumGravityZ, parseFloat(item?.center_of_gravity_z) * parseFloat(item?.mass));
  });

  return {
    center_of_gravity_x: sumGravityX / sumMass,
    center_of_gravity_y: sumGravityY / sumMass,
    center_of_gravity_z: sumGravityZ / sumMass,
  };
};

const formatStringToNumber = (s) => {
  // Trim the input to remove any leading/trailing whitespace
  s = s.trim();

  // Regular expression to check if it's numeric or in "0.x", "-0.x" or ".x", "-.x" format
  if (!/^-?\d*(\.\d+)?$/.test(s)) {
    // Regular expression to extract digits and a single decimal point
    const extracted = s.match(/[\d.]/g);

    if (extracted) {
      // Join the matched parts
      let result = extracted.join("");

      // Ensure only one decimal point is present
      const decimalIdx = result.indexOf(".");
      if (decimalIdx !== -1) {
        result = result.slice(0, decimalIdx + 1) + result.slice(decimalIdx + 1).replace(/\./g, "");
      }

      return result;
    }
    return 0;
  }

  // Special case for zero, handle multiple leading zeros
  if (/^0+(\.0+)?$/.test(s)) {
    return "0";
  }

  // Handle cases like '.0099' and '-.0099'
  if (s.startsWith(".")) {
    return "0" + s;
  }

  if (s.startsWith("-.")) {
    return "-0" + s.slice(1);
  }

  // Special cases for '0', '-0', '0.x', and '-0.x'
  if (s === "0" || s === "-0" || /^-?0\.\d+$/.test(s)) {
    return s;
  }

  // Handle leading zeros for negative numbers
  if (s.startsWith("-")) {
    return "-" + s.slice(1).replace(/^0+/, "");
  }

  // Handle leading zeros for positive numbers
  return s.replace(/^0+/, "");
};

const deepEqual = (x, y) => {
  const isObject = (obj) => obj && typeof obj === "object";

  if (isObject(x) && isObject(y)) {
    if (Object.keys(x).length !== Object.keys(y).length) {
      return false;
    }

    return Object.keys(x).every((key) => deepEqual(x[key], y[key]));
  } else {
    // eslint-disable-next-line
    return x == y;
  }
};

const getClusterCenters = (arr, gap) => {
  arr.sort((a, b) => a.value - b.value);

  const clusters = [];
  let currentCluster = [];

  arr.forEach(({ value }) => {
    if (currentCluster.length === 0) {
      currentCluster.push(value);
      return;
    }

    // For the first cluster, use the first value for gap comparison
    if (clusters.length === 0) {
      const firstValue = currentCluster[0];
      if (value - firstValue <= gap) {
        currentCluster.push(value);
        return;
      }
    } else {
      // For other clusters, use the middle value for gap comparison
      const middleIndex = Math.floor(currentCluster.length / 2);
      const middleValue = currentCluster[middleIndex];
      if (value - middleValue <= gap) {
        currentCluster.push(value);
        return;
      }
    }

    clusters.push([...currentCluster]);
    currentCluster = [value];
  });

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  return clusters.map((cluster, index) => {
    if (index === 0) {
      // For the first cluster, return the first item
      return { value: cluster[0] };
    }
    if (index === clusters.length - 1) {
      // For the last cluster, return the maximum value
      const maxValue = Math.max(...cluster);
      return { value: maxValue };
    }

    // For other clusters, return the middle value
    const middleIndex = Math.floor(cluster.length / 2);
    return { value: cluster[middleIndex] };
  });
};

// This function removes __comment, unnecessary keys, and flattens input_data
const prepareData6DOF = (data) => {
  const clonedData = cloneDeep(data);
  const { input_data, ...restData } = clonedData;

  // Keys to exclude from vehicle data
  const excludedVehicleKeys = ["omega_twd", "ispv", "nozzle_area", "engine_model", "TVC_actuator_model", "RCS_model"];

  // Process vehicle data by excluding specific keys
  const processVehicleData = (vehicle) =>
    Object.fromEntries(Object.entries(vehicle || {}).filter(([key]) => !excludedVehicleKeys.includes(key)));

  // Recursively flatten "value" property and remove "unit" and "__comment"
  const cleanNestedObject = (obj) =>
    Object.fromEntries(
      Object.entries(obj || {}).map(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          const { value: val, ...rest } = value;
          return [key, val !== undefined ? val : cleanNestedObject(rest)];
        }
        return [key, value];
      })
    );

  // Apply transformations
  const cleanedInputData = cleanNestedObject({
    ...omit(input_data, ["IMU_sensor", "controller_settings"]),
    vehicle: processVehicleData(input_data?.vehicle),
  });

  return {
    ...restData,
    ...cleanedInputData,
  };
};

const extractString = (data, fromStr = "", endStr = "") => {
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const pattern = new RegExp(escapeRegExp(fromStr) + "(.*?)" + escapeRegExp(endStr));
  const match = data.match(pattern);

  if (match) {
    return fromStr + match[1];
  }
  return data;
};

const removeDuplicateValues = (data) => {
  const result = {};

  Object.entries(data).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      result[key] = value;
      return;
    }

    // If array contains objects, deduplicate by JSON stringification
    if (value.some((item) => typeof item === "object" && item !== null)) {
      const seen = new Set();
      result[key] = value.filter((item) => {
        const identifier = JSON.stringify(item);
        if (seen.has(identifier)) return false;
        seen.add(identifier);
        return true;
      });
    } else {
      // For arrays of primitives, deduplicate using Set
      result[key] = Array.from(new Set(value));
    }
  });

  return result;
};

/**
 * Updates the state indicating whether each field in `uploadFiles` contains multiple files.
 * If no field contains multiple files, all fields in the state are set to `true`.
 *
 * @param {Object} uploadFiles - An object where keys represent field names and values are arrays of files.
 * @param {Object} uploadMultipleState - An object representing the current state of multiple file uploads.
 * @returns {Object} The updated state indicating whether each field has multiple files.
 */
const updateUploadMultipleState = (uploadFiles, uploadMultipleState) => {
  const updatedState = { ...uploadMultipleState };

  let hasMultipleFiles = false;

  Object.keys(uploadFiles).forEach((field) => {
    const fieldValues = uploadFiles[field] || [];
    updatedState[field] = fieldValues.length > 1;

    if (fieldValues.length > 1) {
      hasMultipleFiles = true;
    }
  });

  if (!hasMultipleFiles) {
    Object.keys(updatedState).forEach((field) => {
      updatedState[field] = true;
    });
  }

  return updatedState;
};

const isDuplicateFileName = (initialFiles, fileName) => {
  if (isEmpty(initialFiles)) return false;
  const existingFiles = initialFiles.map((file) => file?.name || file);

  return existingFiles.some((existing) => {
    const existingFileName = existing?.name || existing;
    return existingFileName.trim().toLowerCase() === fileName.trim().toLowerCase();
  });
};

/**
 * Clone a File properly (not by reference).
 * @param {File} file
 * @returns {File}
 */
const cloneFile = (file) => new File([file], file.name, { type: file.type });

/**
 * Determines the appropriate file path to clone based on the provided value
 * @param {string} value - The file path or file name
 * @param {Object} fallbackItem - The fallback item to get the full path from
 * @param {string} key - The key to look up in the fallback item
 * @returns {string} The file path to clone
 */
const getFilePathToClone = (value, fallbackItem, key) => {
  if (value.includes("/") || !fallbackItem || !fallbackItem[key]) {
    return value;
  }

  return fallbackItem[key];
};

/**
 * Generates new file paths for all string fields in an item
 * @param {Object} item - The item containing file paths
 * @param {Function} generateFilePath - The API function to generate file paths
 * @param {Object} fallbackItem - The fallback item for reference paths
 * @returns {Promise<Object>} Object with new file paths
 */
const generateNewFilePaths = async (item, generateFilePath, fallbackItem) => {
  const newFilePaths = {};

  for (const [key, value] of Object.entries(item)) {
    if (value) {
      const filePathToClone = getFilePathToClone(value?.name || value, fallbackItem, key);
      const apiResponse = await generateFilePath(filePathToClone);
      newFilePaths[key] = apiResponse.name;
    } else {
      newFilePaths[key] = value?.name || value;
    }
  }

  return newFilePaths;
};

/**
 * Gets the raw item for processing, either from existing data or by generating new file paths
 * @param {number} index - The current index
 * @param {number} rawItemCount - The count of raw items available
 * @param {Object} item - The current item being processed
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Function} generateFilePath - The API function to generate file paths
 * @returns {Promise<Object>} The raw item with new file paths to use for processing
 */
const getRawItemForProcessing = async (index, rawItemCount, item, seriesDataRaw, generateFilePath) => {
  if (index < rawItemCount) {
    return seriesDataRaw[index];
  }

  if (!generateFilePath) {
    return seriesDataRaw[0]; // fallback to first item
  }

  try {
    const newFilePaths = await generateNewFilePaths(item, generateFilePath, seriesDataRaw[0]);
    return { ...item, ...newFilePaths };
  } catch (error) {
    return seriesDataRaw[0]; // fallback to first item
  }
};

/**
 * Maps file paths between an item and its corresponding raw item
 * @param {Object} item - The item to process
 * @param {Object} rawItem - The raw item containing reference file paths
 * @returns {Object} The processed item with mapped file paths
 */
const mapFilePathsFromRawItem = (item, rawItem) => {
  const result = { ...item };

  Object.entries(item).forEach(([key, value]) => {
    if (typeof value !== "string") return;

    const fileName = getFileName(value);
    const rawValue = rawItem[key];

    if (typeof rawValue === "string") {
      const rawFileName = getFileName(rawValue);
      const isValuePath = value.includes("/");
      const isRawPath = rawValue.includes("/");

      if (rawFileName === fileName) {
        result[key] = isRawPath ? rawValue : isValuePath ? value : fileName;
      }
    }
  });

  return result;
};

/**
 * Processes a single item in the series data
 * @param {Object} item - The item to process
 * @param {number} index - The index of the item
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Function} generateFilePath - The API function to generate file paths
 * @returns {Promise<Object>} The processed item
 */
const processSingleSeriesItem = async (item, index, seriesDataRaw, generateFilePath) => {
  const rawItemCount = seriesDataRaw.length;
  const rawItem = await getRawItemForProcessing(index, rawItemCount, item, seriesDataRaw, generateFilePath);

  if (!rawItem) {
    return { ...item };
  }

  return mapFilePathsFromRawItem(item, rawItem);
};

/**
 * Maps prefix in series data by processing file paths and generating new ones when needed
 * @param {Array} seriesDataRaw - The raw series data array
 * @param {Array} seriesData - The series data to process
 * @param {Function} generateFilePath - API function to generate file paths
 * @returns {Promise<Array>} The processed series data with mapped file paths
 */
const mappingPrefixInSeriesData6DOF = async (seriesDataRaw, seriesData, generateFilePath) => {
  if (!Array.isArray(seriesDataRaw) || !Array.isArray(seriesData)) {
    return seriesData || [];
  }

  const results = await Promise.all(seriesData.map((item, index) => processSingleSeriesItem(item, index, seriesDataRaw, generateFilePath)));

  return results;
};

const mappingPrefixInSeriesData = (seriesDataRaw, seriesData) => {
  if (!Array.isArray(seriesDataRaw) || !Array.isArray(seriesData)) return seriesData || [];

  const rawItemCount = seriesDataRaw.length;

  return seriesData.map((item, index) => {
    const result = { ...item };
    const rawItem = seriesDataRaw[index] || (index >= rawItemCount ? seriesDataRaw[0] : null);

    if (!rawItem) return result;

    Object.entries(item).forEach(([key, value]) => {
      if (typeof value !== "string") return;

      const fileName = getFileName(value);
      const rawValue = rawItem[key];
      if (typeof rawValue === "string") {
        const rawFileName = getFileName(rawValue);
        const isValuePath = value.includes("/");
        const isRawPath = rawValue.includes("/");

        if (rawFileName === fileName) {
          result[key] = isRawPath ? rawValue : isValuePath ? value : fileName;
        }
      }
    });

    return result;
  });
};

const removeFieldOrObject = (fieldNameSelected, fileNameSelected, rawData, uploadMultiple) => {
  const isMultiple = uploadMultiple[fieldNameSelected];
  const fileNames = Array.isArray(fileNameSelected) ? fileNameSelected : [fileNameSelected];
  const result = cloneDeep(rawData);

  if (isMultiple) {
    if (result.length === 1) {
      // Only one object → just set the field to null if matched
      return result.map((item) => {
        const fieldValue = item[fieldNameSelected];
        const shouldClear = fieldValue && fileNames.some((fileName) => endsWith(fieldValue, fileName));
        if (shouldClear) {
          item[fieldNameSelected] = null;
        }
        return item;
      });
    } else {
      // Multiple objects → filter out objects that MATCH the file name
      return result.filter((item) => {
        const fieldValue = item[fieldNameSelected];
        return !fileNames.some((fileName) => endsWith(fieldValue, fileName));
      });
    }
  } else {
    // Single field → set field to null if matched
    return result.map((item) => {
      const fieldValue = item[fieldNameSelected];
      const shouldClear = fieldValue && fileNames.some((fileName) => endsWith(fieldValue, fileName));
      if (shouldClear) {
        item[fieldNameSelected] = null;
      }
      return item;
    });
  }
};

const findDuplicates = (rootData, newData) => {
  const duplicates = [];

  newData.forEach((newItem) => {
    const isDuplicate = rootData.some((rootItem) => {
      // Check if all fields in newItem have matching values in this rootItem
      return Object.keys(newItem).every((key) => {
        const newValue = newItem[key];
        const rootValue = typeof rootItem[key] === "object" ? rootItem[key]?.name : rootItem[key];

        if (typeof newValue === "object" && newValue !== null) {
          return getFileName(newValue?.name) === getFileName(rootValue);
        }
        return getFileName(newValue) === getFileName(rootValue);
      });
    });

    if (isDuplicate) {
      duplicates.push(newItem);
    }
  });

  return duplicates;
};

const truncateFileName = (fileName, maxLength = 30) => {
  if (!fileName || fileName.length <= maxLength) return fileName;

  // Check if the string contains a dot (indicating it's a file with extension)
  if (!fileName.includes(".")) {
    // No dot found, treat as regular string and truncate without extension
    return `${fileName.slice(0, maxLength - 3)}...`;
  }

  // Has dot, treat as file with extension
  const extension = fileName.split(".").pop();
  const baseName = fileName.slice(0, maxLength - extension.length - 3);
  return `${baseName}...${extension}`;
};

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj || {}, null, 4);
  } catch (error) {
    console.error("JSON stringify failed:", error);
    return "{}";
  }
};

const parseStringToNumberArray = (x, y, z) => [
  typeof x === "string" ? x.split(",").map(Number) : x,
  typeof y === "string" ? y.split(",").map(Number) : y,
  typeof z === "string" ? z.split(",").map(Number) : z,
];

const extractValuesForSimMCS = (source, target, keys, data) => {
  if (data?.MCS?.[source]?.value) {
    keys.forEach((key, index) => {
      target[`${source}_${key}`] = { value: data.MCS[source].value[index] };
    });
  }
};

const extractFormValuesForSimMCS = (source, target, keys, data) => {
  if (data?.MCS?.[source]) {
    keys.forEach((key, index) => {
      target[`${source}_${key}`] = data?.MCS[source]?.[index];
    });
  }
};

const calculateMCSSuccessRate = (caseResultData) => {
  if (!caseResultData || caseResultData.length === 0) return 0;
  const totalCases = caseResultData.length;
  const successfulCases = caseResultData.filter((item) => Number(item["Flight status"]) === 1).length;
  if (successfulCases === 0) return 0;
  const successRate = (successfulCases / totalCases).toFixed(2);
  return successRate;
};

const getFinalStatus = (data, field = "status") => {
  if (data?.has_validation_error && (data[field] === PROCESS_STATUS.SUCCEEDED || data[field] === PROCESS_STATUS.SUCCESSFUL)) {
    return PROCESS_STATUS.FAILED;
  }
  return data[field];
};

/**
 * Returns "Today" with time if the date string matches today's date, otherwise returns the original date string
 * @param {string} dateString - The date string to check (e.g., "2025-07-22 22:18:19")
 * @returns {string} - "Today 22:18:19" if the date is today, otherwise the original date string
 */
const formatUpdateDate = (dateString, translation) => {
  if (!dateString) return "";

  const today = new Date().toISOString().split("T")[0]; // Today in YYYY-MM-DD format
  const [inputDate, inputTime] = dateString.split(" "); // Extract date and time parts

  // If the date part matches today's date, return "Today" with time
  if (inputDate === today) {
    return `${translation("today")} ${inputTime || ""}`.trim();
  }

  return dateString; // Return original date string with time
};

const getRandomColor = () => {
  const colors = ["#fbe3e8", "#f9c5bd", "#fff685", "#00DDFF", "#c2dde6", "#d0bdf4", "#f2d53c", "#ffb766", "#9bc400", "#e1b382"];
  return colors[Math.floor(Math.random() * colors.length)];
};

const transformDataToOptions = (array) => {
  const result = [];

  array.map((item) =>
    result.push({
      label: item.name,
      value: item.id,
    })
  );

  return result;
};

const getTimeSpans = (startTime, endTime) => {
  if (!startTime || !endTime) return "";

  const start = new Date(startTime);
  const end = new Date(endTime);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return "Invalid date";
  }

  const duration = Math.abs(end - start);
  const seconds = Math.floor((duration / 1000) % 60);
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
  const days = Math.floor(duration / (1000 * 60 * 60 * 24));

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0) parts.push(`${seconds}s`);

  return parts.length > 0 ? parts.join(" ") : "0s";
};

const formatDateToYMD = (dateString) => {
  if (!dateString) return "";

  try {
    const date = parseISO(dateString);
    if (!isValid(date)) return "";
    return format(date, DATE_FORMAT.DATE);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

const getTimeFromDateTime = (dateTime) => {
  if (!dateTime) return "";

  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return "";

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error("Error formatting time:", error);
    return "";
  }
};

const getLastTagColor = (tags) => (tags.length > 0 ? tags[tags.length - 1].color : "#00DDFF");

export {
  humanFileSize,
  transformDataVersionsToOptions,
  getFileName,
  getFileType,
  uniqArray,
  handleScrollTo,
  sortData,
  uuid,
  extractIDsFromString,
  transformProcessDataToOptions,
  getConfigFromId,
  getConfigFileName,
  isCSVContent,
  handleScrollToTop,
  transformThrustCalculation,
  reverseTransformThrustCalculation,
  roundToTwoDecimals,
  mergeArrays,
  getChartData,
  uniqueArray,
  MAPPING_CONTENT_CHANGE_TO_TEXT,
  sumArrayWithKey,
  mergeChildren,
  findMaxMin,
  calculateRocketLength,
  calculateProperties,
  calculateCenterOfGravity,
  formatStringToNumber,
  deepEqual,
  getClusterCenters,
  prepareData6DOF,
  truncateFileName,
  getFileUploadName,
  safeStringify,
  parseStringToNumberArray,
  extractValuesForSimMCS,
  calculateMCSSuccessRate,
  extractFormValuesForSimMCS,
  updateUploadMultipleState,
  removeDuplicateValues,
  isDuplicateFileName,
  mappingPrefixInSeriesData,
  mappingPrefixInSeriesData6DOF,
  cloneFile,
  removeFieldOrObject,
  getFilePathToClone,
  generateNewFilePaths,
  getRawItemForProcessing,
  mapFilePathsFromRawItem,
  processSingleSeriesItem,
  findDuplicates,
  getFinalStatus,
  extractString,
  formatUpdateDate,
  getRandomColor,
  transformDataToOptions,
  getTimeSpans,
  formatDateToYMD,
  getLastTagColor,
  getTimeFromDateTime,
};
